import React, { useCallback, useContext, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { TABLET } from "common/constants";
import MyBots from "features/bots/MyBots/MyBots";
import BotsPageWrapper from "features/bots/BotsPageWrapper";
import useMedia from "utils/hooks/useMedia";
import MyBotsPageProvider from "features/bots/MyBotsPageProvider";
import { displayResultMessage, openMessage } from "utils/misc";
import { getEditBotRoute } from "utils/routes";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import BulkDeleteBotsModal from "features/bots/MyBots/BulkDeleteBotsModal";
import BulkUpdateBotsModal from "features/bots/MyBots/BulkUpdateBotsModal";
import { useQueryBotType } from "features/bots/common/hooks";
import { BOTS_PAGE_LAYOUT_CHANGE_KEY } from "features/bots/common/constants";
import useQuotaModal from "features/settings/Quota/useQuotaModal";
import { useLayoutChangeButtons } from "utils/hooks/misc";
import BotsIntroductionModal from "features/bots/common/BotsIntroductionModal";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context.tsx";
import SignalsBotIntroductionModal from "features/bots/common/SignalsBotIntroductionModal.tsx";
import { useRefValue } from "@shared/utils/hooks/misc.ts";
import { BotType } from "@shared/types";

interface BotsProps {
  isChannelConfiguration?: boolean;
  botType?: BotType;
}

const Bots = ({ isChannelConfiguration, botType: propsBotType }: BotsProps) => {
  const [loading, setLoading] = useState(false);
  const { botType: queryBotType } = useQueryBotType();
  const botType = propsBotType || queryBotType;
  const { getUpdatedPath } = useHistoryWithQuery();
  const historyRef = useRefValue(useHistory());
  const { isCardsView, setIsCardsView } = useLayoutChangeButtons(
    BOTS_PAGE_LAYOUT_CHANGE_KEY
  );
  const { search, useMobileSearch } = useContext(PageWrapperContext);
  const [searchValue, setSearchValue] = useState("");
  const searchFilter = useMobileSearch ? search.value : searchValue;

  const useFiltersChips = useMedia(["(max-width: 1860px)"], [true], false);
  const [showFilters, setShowFilters] = useState(!useFiltersChips);

  const showIntroductionModal = useMedia(
    [`(min-width: ${TABLET})`],
    [true],
    false
  );

  const myBots = useMemo(() => {
    return (
      <MyBots
        isCardsView={isCardsView}
        searchValue={searchFilter}
        setLoading={setLoading}
        setShowFilters={setShowFilters}
        setIsCardsView={setIsCardsView}
      />
    );
  }, [isCardsView, searchFilter]);

  const navigateToBulkEdit = useCallback(
    (botsIds: number[]) => {
      const isBulk = botsIds.length > 1;
      if (isBulk) {
        const editBotsPath = getUpdatedPath(
          { botIds: JSON.stringify(botsIds) },
          undefined,
          getEditBotRoute(botType)
        );
        historyRef.current.push(editBotsPath);
      } else {
        historyRef.current.push(getEditBotRoute(botType, botsIds[0]));
      }
    },
    [botType]
  );

  return (
    <MyBotsPageProvider
      openMessage={openMessage}
      botType={botType}
      searchValue={searchFilter}
      navigateToBulkEdit={navigateToBulkEdit}
      displayResultMessage={displayResultMessage}
      BulkDeleteBotsModal={BulkDeleteBotsModal}
      BulkUpdateBotsModal={BulkUpdateBotsModal}
      useQuotaModal={useQuotaModal}
      isChannelConfiguration={isChannelConfiguration}
    >
      <BotsPageWrapper
        isCardsView={isCardsView}
        setIsCardsView={setIsCardsView}
        setSearchValue={setSearchValue}
        loading={loading}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        useFiltersChips={useFiltersChips}
      >
        {myBots}
        {showIntroductionModal && <BotsIntroductionModal botType={botType} />}
        <SignalsBotIntroductionModal />
      </BotsPageWrapper>
    </MyBotsPageProvider>
  );
};

export default Bots;
